import { ChipProps, chipClasses } from '@mui/material/Chip';
import { Theme } from '@mui/material/styles';

import { blueSky, deepNavy, greenScale, lilacScale, redScale, yellowScale } from '../../palette-v2';

// ----------------------------------------------------------------------

type PaletteColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

// NEW VARIANT
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

// Per-color tokens derived from Figma design (soft = Figma "Filled", outlined = Figma "Outlined")
const COLOR_TOKENS: Record<
  PaletteColor,
  { softBg: string; softText: string; outlinedBorder: string; outlinedText: string }
> = {
  primary: {
    softBg: deepNavy[100],
    softText: deepNavy[500],
    outlinedBorder: deepNavy[500],
    outlinedText: deepNavy[800],
  },
  secondary: {
    softBg: lilacScale[100],
    softText: lilacScale[800],
    outlinedBorder: lilacScale[500],
    outlinedText: lilacScale[800],
  },
  success: {
    softBg: greenScale[50],
    softText: greenScale[700],
    outlinedBorder: greenScale[500],
    outlinedText: greenScale[800],
  },
  warning: {
    softBg: yellowScale[50],
    softText: yellowScale[600],
    outlinedBorder: yellowScale[500],
    outlinedText: yellowScale[700],
  },
  error: { softBg: redScale[50], softText: redScale[600], outlinedBorder: redScale[400], outlinedText: redScale[700] },
  info: { softBg: blueSky[50], softText: blueSky[600], outlinedBorder: blueSky[500], outlinedText: blueSky[700] },
};

// Default color (no color prop) → Lilac, matching Figma "Status=Lilac"
const DEFAULT_TOKENS = {
  softBg: lilacScale[100],
  softText: lilacScale[800],
  outlinedBorder: lilacScale[500],
  outlinedText: lilacScale[800],
};

// Disabled tokens
const DISABLED_BG = deepNavy[200];
const DISABLED_TEXT = deepNavy[400];
const DISABLED_BORDER = deepNavy[300];

// ----------------------------------------------------------------------

export function chip(_theme: Theme) {
  const rootStyles = (ownerState: ChipProps) => {
    const defaultColor = ownerState.color === 'default' || !ownerState.color;
    // filled is aliased to soft — only outlined has a distinct look
    const outlinedVariant = ownerState.variant === 'outlined';
    const softVariant = !outlinedVariant;

    const tokens = defaultColor ? DEFAULT_TOKENS : (COLOR_TOKENS[ownerState.color as PaletteColor] ?? DEFAULT_TOKENS);

    const baseStyle = {
      fontWeight: 500,
      borderRadius: '100px',
      [`& .${chipClasses.deleteIcon}`]: {
        opacity: 0.64,
        color: 'currentColor',
        '&:hover': { opacity: 1, color: 'currentColor' },
      },
    };

    const clickable = ownerState.clickable;

    const variantStyle = {
      ...(softVariant && {
        backgroundColor: tokens.softBg,
        color: tokens.softText,
        border: 'none',
        // A deletable chip is interactive even without `clickable`: its delete icon must stay
        // clickable, so only truly inert chips (no click, no delete) are click-through.
        ...(!clickable && !ownerState.onDelete && { pointerEvents: 'none' }),
        ...(clickable && { '&:hover': { backgroundColor: tokens.softBg, filter: 'brightness(0.95)' } }),
        [`& .${chipClasses.icon}`]: { color: tokens.softText },
        [`& .${chipClasses.avatar}`]: { color: tokens.softText, backgroundColor: tokens.softText + '22' },
      }),
      ...(outlinedVariant && {
        border: `1px solid ${tokens.outlinedBorder}`,
        color: tokens.outlinedText,
        backgroundColor: 'transparent',
        ...(!clickable && !ownerState.onDelete && { pointerEvents: 'none' }),
        ...(clickable && { '&:hover': { backgroundColor: tokens.softBg } }),
        [`& .${chipClasses.icon}`]: { color: tokens.outlinedText },
        [`& .${chipClasses.avatar}`]: { color: tokens.outlinedText, backgroundColor: tokens.softBg },
      }),
    };

    const disabledState = {
      [`&.${chipClasses.disabled}`]: {
        opacity: 1,
        color: DISABLED_TEXT,
        [`& .${chipClasses.icon}`]: { color: DISABLED_TEXT },
        [`& .${chipClasses.avatar}`]: { color: DISABLED_TEXT, backgroundColor: DISABLED_BG },
        ...(softVariant && { backgroundColor: DISABLED_BG }),
        ...(outlinedVariant && { borderColor: DISABLED_BORDER, backgroundColor: 'transparent' }),
      },
    };

    return [baseStyle, variantStyle, disabledState];
  };

  return {
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ChipProps }) => rootStyles(ownerState),
      },
    },
  };
}
